import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { LucideAngularModule, MessageSquareHeart, X, Send } from 'lucide-angular';

@Component({
  selector: 'app-dashboard-feedback-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './dashboard-feedback-modal.html',
  styleUrl: './dashboard-feedback-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardFeedbackModalComponent {
  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  protected readonly feedbackText = signal<string>('');
  protected readonly isSubmitting = signal<boolean>(false);
  protected readonly isSubmitted = signal<boolean>(false);

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    if (!value) {
      // Reset state when modal closes
      this.feedbackText.set('');
      this.isSubmitting.set(false);
      this.isSubmitted.set(false);
    }
  }

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly submitted = new EventEmitter<{ feedbackText: string; feedbackType: string }>();

  protected readonly icons = {
    MessageSquareHeart,
    X,
    Send,
  };

  protected close(): void {
    this.closed.emit();
  }

  protected onFeedbackInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.feedbackText.set(target.value);
  }

  protected submitFeedback(): void {
    const text = this.feedbackText().trim();
    if (!text) {
      return;
    }

    this.isSubmitting.set(true);
    this.submitted.emit({
      feedbackText: text,
      feedbackType: 'dashboard_general_feedback',
    });
  }

  // Called by parent after successful submission
  public showSuccess(): void {
    this.isSubmitting.set(false);
    this.isSubmitted.set(true);
    
    // Auto-close after showing success message
    setTimeout(() => {
      this.close();
    }, 2000);
  }

  // Called by parent on error
  public resetSubmitting(): void {
    this.isSubmitting.set(false);
  }
}
